/* eslint-disable react/jsx-props-no-spreading */
import { Flex, Stack, Switch, Text } from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuthStore } from '@/client/services/state/authStore';
import { useEffect } from 'react';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';
import v3ApiService from '@/client/services/api/clients/v3ApiClient';

export default function NotificationPreferencesForm() {
  const { t } = useTranslation();
  const { setToast } = useToastStore();
  const { authConfig } = useAuthStore();
  const { user } = authConfig;
  const { _id: userId } = user;
  const queryClient = useQueryClient();

  const methods = useForm({
    defaultValues: {
      email: false,
      app: false,
    },
  });

  const { setValue, reset, watch } = methods;

  const { email, app } = watch();

  const { data } = useQuery({
    queryKey: ['notifications-preferences', userId],
    queryFn: () => v3ApiService.getUserNotificationPreferences(userId),
  });

  useEffect(() => {
    if (data) {
      reset({
        email: data.notifications.email,
        app: data.notifications.app,
      });
    }
  }, [data]);

  const updateNotificationsPreferences = useMutation({
    mutationFn: () => v3ApiService.updateUserNotificationPreferences(userId, email, app),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['notifications-preferences'] });

      setToast({
        show: true,
        status: 'success',
        title: t('modules.notifications.haveBeenUpdated'),
      });
    },

    onError: () =>
      setToast({
        show: true,
        status: 'error',
        title: t('modules.notifications.canNotUpdateNotifications'),
      }),
  });

  const { status } = updateNotificationsPreferences;

  const onHandleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>, field: 'email' | 'app') => {
    const value = e.target.checked;

    setValue(field, value);

    updateNotificationsPreferences.mutateAsync();
  };

  return (
    <FormProvider {...methods}>
      <Stack spacing={4}>
        <Text variant="formSectionHeading">
          {t('learners.profile.settings.notifications.title')}
        </Text>

        <Flex justifyContent="space-between" alignItems="center">
          <Text variant="formlabel">{t('learners.profile.settings.notifications.email')}</Text>

          <Switch
            isDisabled={status === 'loading'}
            isChecked={email}
            id="email"
            variant="warmNeutral"
            onChange={(e) => onHandleSwitchChange(e, 'email')}
          />
        </Flex>

        <Flex justifyContent="space-between" alignItems="center">
          <Text variant="formlabel">{t('learners.profile.settings.notifications.app')}</Text>

          <Switch
            isDisabled={status === 'loading'}
            isChecked={app}
            id="app"
            variant="warmNeutral"
            onChange={(e) => onHandleSwitchChange(e, 'app')}
          />
        </Flex>
      </Stack>
    </FormProvider>
  );
}
